import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './ReviewHeader.scss';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as UploadIcon} from '../../../../assets/images/upload.svg';
import {ReactComponent as AddIcon} from '../../../../assets/images/add.svg';
import {ReactComponent as ManageProtocol} from '../../../../assets/images/manageProtocol.svg';
import React, {createContext, useContext, useEffect, useState} from 'react';
import CreateNewJob from '../create-job/CreateNewJob';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ProtocolIntelligence from '../protocolIntelligence/ProtocolIntelligence';
import ReviewAndApprove from '../review-and-approve/ReviewAndApprove';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ToastType} from '../../../../shared/constants';
import ApprovedProtocolActions from '../approved-protocol-actions/ApprovedProtocolActions';
import {
  useGetAllProtocolIntelligenceIds,
  useGetProtocolIntelligenceDetails,
  useExportAIReviewJobs,
} from '../../../../hooks/ai/review/useReview';
import {
  IAIReviewProtocolIntelligenceResponse,
  IAIReviewJobsExportRequest,
  ProtocolIntelligenceStatusTypes,
  ProtocolIntelligenceStatus,
} from '../../../../api/types/aiReview.type';
import EditProtocolIntelligence from '../protocolIntelligence/editProtocolIntelligence/EditProtocolIntelligence';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';

export type ReviewHeaderToastType = {
  isOpen: boolean;
  message: string;
  status: ToastType;
};

type ReviewHeaderContextType = {
  isManageProtocolOpen: boolean;
  handleManageProtocolClose: () => void;
  handleShowReviewAndApproveOpen: () => void;
  uploadedFile: File | null;
  isArtifactsGenerated: boolean;
  isFileSubmitted: boolean;
  setIsFileSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  isArifactReviewed: boolean;
  setUploadedFile: React.Dispatch<React.SetStateAction<File | null>>;
  handleApproveArtifacts: () => void;
  setReviewHeaderToast: React.Dispatch<
    React.SetStateAction<ReviewHeaderToastType>
  >;
  setToIsApprovalPendingState: () => void;
  hasValidationErrors: boolean;
  setHasValidationErrors: React.Dispatch<React.SetStateAction<boolean>>;
  isEditingProtocol: boolean;
  setIsEditingProtocol: React.Dispatch<React.SetStateAction<boolean>>;
  isApproved: boolean;
  setIsApproved: React.Dispatch<React.SetStateAction<boolean>>;
  fileName: string | null;
  lastModifiedAt: string | null;
  lastModifiedByUserId: string | null;
  uploadedAt: string | null;
  setUploadedAt: React.Dispatch<React.SetStateAction<string | null>>;
  isProtocolActive: boolean;
  setIsProtocolActive: React.Dispatch<React.SetStateAction<boolean>>;
  handlReviewHeaderToastClose: () => void;
  refetchProtocolData: () => void;
  refetchProtocolIds: () => void;
  protocolDetails: IAIReviewProtocolIntelligenceResponse | null;
  showEditProtocolIntelligence: boolean;
  setShowEditProtocolIntelligence: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  resetStates: () => void;
};

export const ReviewHeaderContext =
  createContext<ReviewHeaderContextType | null>(null);

export const useReviewHeaderContext = () => {
  const ctx = useContext(ReviewHeaderContext);
  if (!ctx) {
    throw new Error('useReviewHeaderContext must be used within a Provider');
  }
  return ctx;
};

const reviewHeaderToastDefaultValue = {
  isOpen: false,
  message: '',
  status: ToastType.Success,
};

// ============================ReviewHeader Component==============================

type ReviewHeaderProps = {
  hasJobsForExport?: boolean;
  jobExportPayload?: IAIReviewJobsExportRequest;
  isCreateJob: boolean;
  setIsCreateJob: React.Dispatch<React.SetStateAction<boolean>>;
};

const ReviewHeader: React.FC<ReviewHeaderProps> = ({
  hasJobsForExport = false,
  jobExportPayload,
  isCreateJob,
  setIsCreateJob,
}) => {
  // Waht are these states used for isCreateJob and isJobList ????????????????????????
  const [showReviewAndApprove, setShowReviewAndApprove] = useState(false);
  const [isManageProtocolOpen, setIsManageProtocolOpen] = useState(false);
  const [reviewHeaderToast, setReviewHeaderToast] =
    useState<ReviewHeaderToastType>(reviewHeaderToastDefaultValue);
  // Files state attributes
  const [isProtocolActive, setIsProtocolActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [lastModifiedAt, setLastModifiedAt] = useState<string | null>(null);
  const [lastModifiedByUserId, setLastModifiedByUserId] = useState<
    string | null
  >(null);
  const [uploadedAt, setUploadedAt] = useState<string | null>(null);
  // This state helps to hadle the state form API w.r.t Artifacts-Generation-Status, Need to be mapped with API response during it's integration
  const [isArtifactsGenerated, setIsArtifactsGenerated] = useState(false);
  // This state helps to hadle the state form API w.r.t Artifacts-Reviewed-Status, Need to be mapped with API response during it's integration
  const [isArifactReviewed, setIsArtifactReviewed] = useState(false);
  // This state helps to hadle the state form API w.r.t fileSubmitted to GenerateArtifacts, Need to be mapped with API response during it's integration
  const [isFileSubmitted, setIsFileSubmitted] = useState(false);
  // This state helps to check for any validation errors
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [isEditingProtocol, setIsEditingProtocol] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  // This state helps to EDIT the Protocol Intelligence data
  const [showEditProtocolIntelligence, setShowEditProtocolIntelligence] =
    useState(false);
  const [isApiError, setIsApiError] = useState(false);

  const {mutateAsync: exportJobs, isPending: isExportingJobs} =
    useExportAIReviewJobs();
  // Fetch ProtocolIds list
  const {
    data: protocolIds = [],
    refetch: refetchProtocolIds,
    error: protocolIdsError,
    isFetching: isFetchingProtocolIds,
  } = useGetAllProtocolIntelligenceIds(isManageProtocolOpen);

  //Prevent stale cached ids from being reused during delete/refetch cycle
  const stableProtocolIds = isFetchingProtocolIds ? [] : protocolIds;

  const firstProtocolId = stableProtocolIds?.[0]?.protocolId;

  const {
    data: protocolDetails = null,
    refetch: refetchProtocolData,
    error: protocolDetailsError,
  } = useGetProtocolIntelligenceDetails(
    isManageProtocolOpen ? firstProtocolId : undefined
  );

  useEffect(() => {
    if (protocolIdsError || protocolDetailsError) {
      setIsApiError(true);
    }
  }, [protocolIdsError, protocolDetailsError]);

  const retryProtocolApis = async () => {
    try {
      if (protocolIdsError) {
        await refetchProtocolIds();
      }
      if (protocolDetailsError && firstProtocolId) {
        await refetchProtocolData();
      }
      setIsApiError(false);
    } catch (error) {
      console.error('Retry failed', error);
      setIsApiError(true);
    }
  };

  const onProtocolRefreshClick = () => {
    retryProtocolApis();
  };

  const setTheFlowFlags = () => {
    // Setting Functional flow validator step-flags in GenerateArtifacts.tsx
    if (protocolDetails?.protocolId) {
      setIsFileSubmitted(true);
      // Need to remove the hardcoding once API integration is done and map these states with API response
      setFileName(protocolDetails?.fileName ?? 'no-file-name');
      setLastModifiedAt(protocolDetails?.lastModifiedAt ?? null);
      setLastModifiedByUserId(protocolDetails?.lastModifiedByUserId ?? '');
      setUploadedAt(protocolDetails?.uploadedAt ?? null);
      setIsProtocolActive(protocolDetails?.isActive ?? false);
      if (
        protocolDetails.aIstatus === ProtocolIntelligenceStatusTypes.PENDING
      ) {
        // Step2
        setIsArtifactsGenerated(false);
        setIsArtifactReviewed(false);
      } else if (
        protocolDetails.aIstatus === ProtocolIntelligenceStatusTypes.COMPLETED
      ) {
        if (protocolDetails?.isApproved) {
          // Step4
          setIsArtifactsGenerated(true);
          setIsArtifactReviewed(true);
          setIsApproved(true);
        } else {
          // Step3
          setIsArtifactsGenerated(true);
          setIsArtifactReviewed(false);
        }
      }
    }
  };

  const pollingStatuses = [ProtocolIntelligenceStatusTypes.PENDING] as const;

  type PollingStatus = (typeof pollingStatuses)[number];

  const isPollingStatus = (
    status: ProtocolIntelligenceStatus | undefined
  ): status is PollingStatus => {
    return !!status && pollingStatuses.includes(status as PollingStatus);
  };

  useEffect(() => {
    const status = protocolDetails?.aIstatus;
    if (!isPollingStatus(status) || !isManageProtocolOpen) return;
    const intervalId = setInterval(() => {
      refetchProtocolData();
    }, 10000);
    // Clear the interval on component unmount or when dependencies change to avoid memory leaks and unnecessary API calls
    return () => clearInterval(intervalId);
  }, [protocolDetails?.aIstatus, isManageProtocolOpen, refetchProtocolData]);

  useEffect(() => {
    if (protocolDetails && isManageProtocolOpen) {
      setTheFlowFlags();
    }
  }, [protocolDetails, isManageProtocolOpen]);

  useEffect(() => {
    setFileName(uploadedFile?.name ?? null);
  }, [uploadedFile?.name]);

  const handleManageProtocolOpen = () => {
    setIsManageProtocolOpen(true);
  };

  const resetStates = () => {
    setShowReviewAndApprove(false);
    setIsArtifactReviewed(false);
    setUploadedFile(null);
    setReviewHeaderToast(reviewHeaderToastDefaultValue);
    setIsFileSubmitted(false);
    setIsArtifactsGenerated(false);
    setIsApproved(false);
    setIsEditingProtocol(false);
    setFileName(null);
    setLastModifiedAt(null);
    setLastModifiedByUserId(null);
    setUploadedAt(null);
    setShowEditProtocolIntelligence(false);
  };

  const handleManageProtocolClose = () => {
    setIsManageProtocolOpen(false);
    resetStates();
  };

  const handleShowReviewAndApproveOpen = () => {
    setShowReviewAndApprove(true);
  };

  const handleApproveArtifacts = () => {
    setShowReviewAndApprove(false);
    setIsArtifactReviewed(true);
    setIsApproved(true);
  };

  const setToIsApprovalPendingState = () => {
    setIsArtifactReviewed(false);
    setIsArtifactsGenerated(true);
    setIsFileSubmitted(true);
    setShowReviewAndApprove(false);
  };

  const handlReviewHeaderToastClose = () => {
    setReviewHeaderToast(reviewHeaderToastDefaultValue);
  };

  const handleExportJobs = async () => {
    if (!hasJobsForExport || !jobExportPayload) return;
    const exportRequest: IAIReviewJobsExportRequest = {};
    if (
      jobExportPayload.filters &&
      Object.keys(jobExportPayload.filters).length
    ) {
      exportRequest.filters = jobExportPayload.filters;
    }
    if (jobExportPayload.sort?.field) {
      exportRequest.sort = jobExportPayload.sort;
    }
    if (
      jobExportPayload.pagination?.page &&
      jobExportPayload.pagination?.pageSize
    ) {
      exportRequest.pagination = jobExportPayload.pagination;
    }
    try {
      const blob = await exportJobs(exportRequest);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AIReviewJobs_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      setReviewHeaderToast({
        isOpen: true,
        status: ToastType.Error,
        message: t('ai.review.header.exportFailed'),
      });
    }
  };

  const renderProtocolIntelligenceContent = () => {
    // Sequentially check the flags to decide which component to render in the drawer.
    // Please do not change the order of these checks as they are based on the functional flow of Protocol Intelligence.
    if (showEditProtocolIntelligence) {
      return <EditProtocolIntelligence />;
    }
    if (showReviewAndApprove) {
      return <ReviewAndApprove />;
    }
    if (isEditingProtocol) {
      return <ApprovedProtocolActions protocolId={firstProtocolId} />;
    }
    return (
      <ProtocolIntelligence
        isApiError={isApiError}
        onProtocolRefreshClick={onProtocolRefreshClick}
      />
    );
  };

  return (
    <Box className="review-header-container">
      <Typography
        className="typo-text subhead1_size"
        variant="h1"
        tabIndex={0}
        ara-label={t('ai.review.header.title')}
        data-testid={`review-header-title`}
      >
        {t('ai.review.header.title')}
        <ArrTooltipWrapper
          trigger={
            <InfoIcon
              className="info-icon-label"
              data-testid="toggle-info-tooltip"
            />
          }
          title={t('ai.review.header.title')}
        >
          <Box
            dangerouslySetInnerHTML={{
              __html: t('ai.review.header.titleTooltipText'),
            }}
            data-testid={`ai-review-header-title-tooltip`}
          ></Box>
        </ArrTooltipWrapper>
      </Typography>
      <Box className="header-action">
        <ArrIconButton
          icon={
            <UploadIcon
              data-testid="upload-icon"
              className={!hasJobsForExport ? 'upload-icon-disabled' : undefined}
            />
          }
          ariaLabel={'ai.review.header.uploadIcon'}
          className="upload-icon-button"
          testId="arr-icon-button"
          disabled={!hasJobsForExport || isExportingJobs}
          tabIndex={0}
          onClick={handleExportJobs}
        />
        <ArrButton
          variant="text"
          size="medium"
          disabled={false}
          aria-label={t('ai.review.header.manageProtocol')}
          dataTestId="manageProtocol"
          className="manage-protocol-button"
          onClick={handleManageProtocolOpen}
        >
          <ManageProtocol
            className="manage-protocol-btn-icon"
            data-testid="manage-protocol-icon"
          />
          {t('ai.review.header.manageProtocol')}
        </ArrButton>

        <ArrButton
          variant="primary"
          size="medium"
          disabled={false}
          aria-label={t('ai.review.header.newJob')}
          dataTestId="create-new-job"
          className="new-job-button"
          onClick={() => setIsCreateJob(true)}
        >
          {t('ai.review.header.newJob')}
          <AddIcon className="add-icon" data-testid="add-icon" />
        </ArrButton>
      </Box>
      {isCreateJob && (
        <CreateNewJob handleJobTypeModalClose={() => setIsCreateJob(false)} />
      )}
      <ArrDrawer
        isOpen={isManageProtocolOpen || showReviewAndApprove}
        ariaLabel="drawer content"
        onClose={handleManageProtocolClose}
        size="small"
        side="right"
        showButton={false}
        showHeader={false}
        className="manage-protocol-drawer"
      >
        <ReviewHeaderContext.Provider
          value={{
            isManageProtocolOpen,
            handleManageProtocolClose,
            handleShowReviewAndApproveOpen,
            uploadedFile,
            isArtifactsGenerated,
            isFileSubmitted,
            setIsFileSubmitted,
            isArifactReviewed,
            setUploadedFile,
            handleApproveArtifacts,
            setReviewHeaderToast,
            setToIsApprovalPendingState,
            isApproved,
            setIsApproved,
            hasValidationErrors,
            setHasValidationErrors,
            isEditingProtocol,
            setIsEditingProtocol,
            fileName,
            lastModifiedAt,
            lastModifiedByUserId,
            uploadedAt,
            setUploadedAt,
            isProtocolActive,
            setIsProtocolActive,
            handlReviewHeaderToastClose,
            refetchProtocolData,
            refetchProtocolIds,
            protocolDetails,
            setShowEditProtocolIntelligence,
            showEditProtocolIntelligence,
            resetStates,
          }}
        >
          <Box
            className="review-header-sr-only"
            aria-live="polite"
            aria-atomic="true"
            data-testid="toast-live-region"
          />
          {renderProtocolIntelligenceContent()}
        </ReviewHeaderContext.Provider>
      </ArrDrawer>
      {reviewHeaderToast.isOpen && (
        <ArrToastNotification
          message={reviewHeaderToast.message}
          onClose={handlReviewHeaderToastClose}
          status={[reviewHeaderToast.status]}
          data-testid="toast-message"
          aria-label={reviewHeaderToast.message}
        />
      )}
      <ArrLoadingSpinner
        overlay
        isOpen={isExportingJobs}
        show={isExportingJobs}
        ariaLabel={t('common.loading')}
      />
    </Box>
  );
};

export default ReviewHeader;
